import React, { useEffect } from "react";
import { useHotkeys } from "react-hotkeys-hook";
import { useSelector } from "react-redux";

import { booleanOverlap, difference } from "@turf/turf";
import { RootState } from "store/rootReducer";
import { rgbStringToHex } from "utils/color/rgbHelpers";

import { useMapContext } from "components/map/hooks/useMapContext";

import colorPolygons from "./helpers/colorPolygons";
import { parseGeom } from "./helpers/parseGeom";
import { useGeomBinContext } from "./hooks/useGeomBinContext";
import { useGeomBinDispatch } from "./hooks/useGeomBinDispatch";
import { IGeomBinItem } from "./models/shared.models";

export interface LassoSelectionComponentModel {
  drawRef;
  portalRef?: React.MutableRefObject<HTMLDivElement>;
}

// mapbox-gl-draw modes where a shape is actively being drawn (not yet
// committed). Escape should only abort these — never a select mode.
const IN_PROGRESS_DRAW_MODES = ["draw_polygon", "draw_circle", "draw_rectangle"];

// booleanOverlap/difference only accept Polygon/MultiPolygon features; the
// stored geom is trusted (as it always has been) to be one of those.
type PolygonFeature = GeoJSON.Feature<GeoJSON.Polygon | GeoJSON.MultiPolygon>;

export default function GeomBinDrawer({
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  portalRef,
  drawRef
}: LassoSelectionComponentModel): JSX.Element {
  const { mapbox } = useMapContext();
  const geomBinDispatch = useGeomBinDispatch();
  const {
    isActive: isGeomBinOpen,
    geomBinItems,
    selectedGeomBinItem
  } = useGeomBinContext();
  const activeColorPalette = useSelector(
    (state: RootState) => state.userSetting.activeColorPalette
  );

  // isGeomBinOpen MUST be in the deps: react-hotkeys-hook memoizes the handler
  // on this array, so without it the bound closure keeps the isGeomBinOpen value
  // from map-init (false) and the isGeomBinOpen gate inside cancelPolygonSelection
  // would read stale — making Escape-to-discard a silent no-op once the drawer
  // opens (mapbox is unchanged by then). Mirrors LassoSelection/XdaLineMarker.
  useHotkeys(
    "esc",
    () => {
      cancelPolygonSelection();
    },
    {},
    [mapbox, isGeomBinOpen]
  );

  function cancelPolygonSelection() {
    // GeomBinDrawer is always mounted (Map.tsx), so this Escape handler runs
    // even when the geom-bin editor is closed. The in-progress draw modes below
    // are the SAME mapbox-gl-draw mode names the well-selection lasso
    // (LassoSelection) uses, so without gating on isGeomBinOpen an Escape during
    // a lasso draw would fire trash() and write a stale isLassoSelected into the
    // geom-bin context. Only act while the geom-bin editor is actually open.
    if (!drawRef?.current || !isGeomBinOpen) {
      return;
    }
    try {
      const mode = drawRef.current.getMode();

      // Only abort when a new shape is actively being drawn. trash() deletes
      // the in-progress feature and exits to simple_select WITHOUT committing
      // it — unlike changeMode("simple_select"), which runs the draw mode's
      // onStop and would keep a valid partial polygon, creating an unwanted new
      // polygon. It only affects the feature being drawn, so already-committed
      // polygons are untouched. When a committed polygon is merely selected
      // (simple_select/direct_select) or nothing is in progress, do nothing so
      // Escape can never delete a committed polygon.
      if (IN_PROGRESS_DRAW_MODES.includes(mode)) {
        drawRef.current.trash();
        geomBinDispatch({
          payload: {
            isLassoSelected: false
          }
        });
      }

      // drawRef.current.deleteAll();
      // updateSelectionCentroid(mapbox, null);
      // mapDispatch({
      //   payload: {
      //     selectedFeatures: []
      //   }
      // });
      // setLassoSelect(false);

      // geomBinDispatch({
      //   payload: {
      //     isLassoSelected: false,
      //     geomBinItems: [...geomBinItems]
      //   }
      // });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(err);
    }
  }

  useEffect(() => {
    if (!drawRef?.current) {
      return;
    }

    drawRef.current.deleteAll();
  }, [drawRef, isGeomBinOpen]);

  useEffect(() => {
    if (geomBinItems.some((item) => !item.color)) {
      // Some polygons didn't have color, trigger to fill in color for the polygon
      const coloredGeomBinItems = colorPolygons(
        activeColorPalette,
        geomBinItems,
        geomBinDispatch,
        null
      );
      if (coloredGeomBinItems) {
        geomBinDispatch({
          payload: {
            geomBinItems: coloredGeomBinItems
          }
        });
      }
      return;
    }

    if (!drawRef?.current) {
      return;
    }

    drawRef.current.deleteAll();

    geomBinItems.forEach((item) => {
      const feature = parseGeom(item.geom);
      if (!feature) {
        // Unusable geometry (null/malformed/no id) — skip drawing this item
        // rather than throwing into the map's ErrorBoundary. It stays in
        // geomBinItems / the side panel, just isn't rendered on the map.
        return;
      }
      // feature.properties.color = item.color;
      drawRef.current.add(feature);
      drawRef.current.setFeatureProperty(feature.id, "portColor", item.color);
      drawRef.current.setFeatureProperty(feature.id, "portStroke", item.strokeColor);
      drawRef.current.setFeatureProperty(feature.id, "portOpacity", item.opacity);
      drawRef.current.setFeatureProperty(feature.id, "portThickness", item.thickness);
    });
  }, [geomBinItems]);

  const onDrawEnd = (e) => {
    //calls when finished drawing
    if (e.features.length > 0) {
      const feature = e.features[0];

      const coloredGeomBinItems = colorPolygons(
        activeColorPalette,
        geomBinItems,
        geomBinDispatch,
        feature
      );

      if (coloredGeomBinItems) {
        const newGeomBinItems = coloredGeomBinItems
          .map((item) => {
            const parsed = parseGeom(item.geom);

            if (
              item.id !== feature.id &&
              parsed &&
              booleanOverlap(feature, parsed as unknown as PolygonFeature)
            ) {
              // if the existing polygon overlap with selected polygon
              // trim the existing polygon
              const trim = difference(parsed as unknown as PolygonFeature, feature);
              if (!trim) {
                // Existing polygon fully consumed (zero remaining area) —
                // drop it rather than store the string "null" as geom.
                return null;
              }
              trim.id = item.id;
              return {
                id: item.id,
                name: item.name,
                geom: JSON.stringify(trim),
                order: item.order,
                color: rgbStringToHex(item.color),
                strokeColor: rgbStringToHex(item.strokeColor),
                opacity: item.opacity,
                thickness: item.thickness
              };
            }

            return item;
          })
          .filter((item): item is IGeomBinItem => item !== null);

        geomBinDispatch({
          payload: {
            geomBinItems: newGeomBinItems,
            isLassoSelected: false
          }
        });
      }
    }
  };

  async function onMouseUp() {
    if (!drawRef.current) {
      return;
    }

    const selected = drawRef.current.getSelected();

    if (!selected?.features?.length) {
      return;
    }

    const hasChanged = geomBinItems.some((item) => {
      const parsed = parseGeom(item.geom);
      return (
        item.id === selected.features[0].id &&
        JSON.stringify(parsed?.geometry) !== JSON.stringify(selected.features[0].geometry)
      );
    });

    if (!hasChanged) {
      return;
    }

    const newGeomBinItems = geomBinItems
      .map((item) => {
        if (item.id === selected.features[0].id) {
          return {
            id: item.id,
            name: item.name,
            geom: JSON.stringify(selected.features[0]),
            order: item.order,
            color: rgbStringToHex(item.color),
            strokeColor: rgbStringToHex(item.strokeColor),
            opacity: item.opacity,
            thickness: item.thickness
          };
        }

        const parsed = parseGeom(item.geom);

        if (
          parsed &&
          booleanOverlap(selected.features[0], parsed as unknown as PolygonFeature)
        ) {
          // if the existing polygon overlap with selected polygon
          // trim the existing polygon
          const trim = difference(
            parsed as unknown as PolygonFeature,
            selected.features[0]
          );
          if (!trim) {
            // Existing polygon fully consumed (zero remaining area) —
            // drop it rather than store the string "null" as geom.
            return null;
          }
          trim.id = item.id;
          return {
            id: item.id,
            name: item.name,
            geom: JSON.stringify(trim),
            order: item.order,
            color: rgbStringToHex(item.color),
            strokeColor: rgbStringToHex(item.strokeColor),
            opacity: item.opacity,
            thickness: item.thickness
          };
        }

        return item;
      })
      .filter((item): item is IGeomBinItem => item !== null);

    geomBinDispatch({
      payload: {
        geomBinItems: newGeomBinItems,
        selectedGeomBinItem: undefined
      }
    });

    setTimeout(function () {
      geomBinDispatch({
        payload: {
          selectedGeomBinItem: selected.features[0].id
        }
      });
    }, 100);
  }

  async function onPolygonSelected() {
    if (!drawRef?.current) {
      return;
    }

    const selected = drawRef.current.getSelected();

    if (selected?.features[0]?.id === selectedGeomBinItem) {
      return;
    }

    if (selected?.features[0]?.id) {
      drawRef.current.changeMode("direct_select", {
        featureId: selected?.features[0]?.id
      });
    }

    geomBinDispatch({
      payload: {
        selectedGeomBinItem: selected?.features[0]?.id
      }
    });
  }

  useEffect(() => {
    if (!mapbox || !isGeomBinOpen) {
      // Do not subscribe to map events when geom bin drawer is closed
      return;
    }

    mapbox.on("draw.create", onDrawEnd);
    mapbox.on("click", onPolygonSelected);
    mapbox.on("mouseup", onMouseUp);
    mapbox.on("touchend", onMouseUp);
    return () => {
      mapbox.off("draw.create", onDrawEnd);
      mapbox.off("click", onPolygonSelected);
      mapbox.off("mouseup", onMouseUp);
      mapbox.off("touchend", onMouseUp);
    };
  }, [mapbox, geomBinItems, isGeomBinOpen]);

  useEffect(() => {
    if (!drawRef?.current) {
      return;
    }

    if (!selectedGeomBinItem) {
      drawRef.current.changeMode("simple_select", { featureIds: [] });
      return;
    }

    const selected = drawRef.current.getSelected();

    if (selected?.features[0]?.id === selectedGeomBinItem) {
      // if the polygon is already selected, move on
      return;
    }

    // The selected item may have no matching feature on the draw control —
    // e.g. its geometry was unusable and skipped by the re-add effect, or it
    // was dropped after being fully consumed by an overlap trim. Entering
    // direct_select for a missing feature throws "You must provide a featureId
    // to enter direct_select mode", which crashes the map into the
    // "Click to Retry" ErrorBoundary. Guard it: clear the selection instead.
    if (!drawRef.current.get(selectedGeomBinItem)) {
      drawRef.current.changeMode("simple_select", { featureIds: [] });
      return;
    }

    // select the polygon
    drawRef.current.changeMode("direct_select", { featureId: selectedGeomBinItem });
  }, [selectedGeomBinItem]);

  return <></>;
}
